import { useNavigate } from "react-router-dom";
import { fmtDT, fmtDay } from "@/lib/contactsApi";
import { ROLE_LABEL, STATUS_LABEL } from "@/lib/access";
import { TestBadge } from "@/components/TestMode";
import { BrandName } from "@/lib/brand";

function Section({ title, count, children, testId }) {
  return (
    <section data-testid={testId}>
      <h4 className="text-sm font-semibold mb-1.5">{title}{count != null && <span className="text-stone-500 font-normal"> ({count})</span>}</h4>
      {children}
    </section>
  );
}

const li = "rounded-lg border border-stone-200 px-2.5 py-2 text-xs";
const Empty = ({ t }) => <p className="text-xs text-stone-500">{t}</p>;
const AUDIT = { contact_edited: "Contact details edited", role_changed: "Role changed", status_changed: "Status changed", deleted: "Moved to Deleted Contacts",
  restored: "Restored", sign_in_link_sent: "Sign-in link sent", permanently_deleted: "Permanently deleted" };
const fmtVal = (v) => (v == null ? "—" : typeof v === "object" ? Object.entries(v).map(([k, x]) => `${k}: ${Array.isArray(x) ? x.join(", ") || "none" : (ROLE_LABEL[x] || STATUS_LABEL[x] || x) ?? "—"}`).join(" · ") : String(v));

export function AccountRecords({ a }) {
  const navigate = useNavigate();
  return (
    <div className="space-y-4">
      <Section title="Horses" count={a.horses.length} testId="account-horses">
        {a.horses.length ? <ul className="space-y-1">{a.horses.map((h) => (
          <li key={h.id}><button type="button" onClick={() => navigate(`/horse/${h.id}`)} className={`${li} w-full text-left hover:bg-stone-50 flex items-center gap-1.5`}>
            {h.is_test && <TestBadge />}<span className="font-semibold">{h.name || "Unnamed horse"}</span><span className="text-stone-500">· {(h.status || "").replace(/_/g, " ")}</span></button></li>
        ))}</ul> : <Empty t="No horses." />}
      </Section>
      <Section title="Applications" count={a.applications.length} testId="account-applications">
        {a.applications.length ? <ul className="space-y-1">{a.applications.map((x) => (
          <li key={x.id}><button type="button" onClick={() => navigate(`/applications/${x.id}`)} className={`${li} w-full text-left hover:bg-stone-50 flex items-center gap-1.5`}>
            {x.is_test && <TestBadge />}<span className="font-semibold">{x.horse_name || "Unnamed horse"}</span><span className="text-stone-500">· {x.status.replace(/_/g, " ")}{x.submitted_at ? ` · ${fmtDay(x.submitted_at)}` : ""}</span></button></li>
        ))}</ul> : <Empty t="No applications." />}
      </Section>
      <Section title="Signed contracts" count={a.contracts.length} testId="account-contracts">
        {a.contracts.length ? <ul className="space-y-1">{a.contracts.map((k) => (
          <li key={k.id}><button type="button" onClick={() => navigate(`/applications/${k.application_id}#agreement`)} className={`${li} w-full text-left hover:bg-stone-50`}>
            Version {k.version} · signed by {k.signer_name || "—"} · {fmtDT(k.signed_at)}</button></li>
        ))}</ul> : <Empty t="No signed contracts." />}
      </Section>
      <Section title="Recent messages" count={a.messages.length} testId="account-messages">
        {a.messages.length ? <ul className="space-y-1">{a.messages.map((m) => (
          <li key={m.id} className={li}><div className="text-stone-500">{m.sender_type === "admin" ? <BrandName /> : m.sender_name || "Seller"} · {fmtDT(m.created_at)}</div><div className="mt-0.5 break-words">{m.body}</div></li>
        ))}</ul> : <Empty t="No messages." />}
      </Section>
      <Section title="Login history" count={a.login_history.length} testId="account-logins">
        {a.login_history.length ? <ul className="space-y-1">{a.login_history.map((s, i) => (
          <li key={i} className={li}>Signed in {fmtDT(s.created_at)} · last active {fmtDT(s.last_seen)}{s.revoked ? ` · ended${s.revoke_reason ? ` (${s.revoke_reason})` : ""}` : ""}
            <div className="text-stone-400 truncate">{s.ip || ""} {s.user_agent || ""}</div></li>
        ))}</ul> : <Empty t="Never signed in." />}
      </Section>
      {a.audit && (
        <Section title="Account activity (audit history)" count={a.audit.length} testId="account-audit">
          {a.audit.length ? <ul className="space-y-1">{a.audit.map((e) => (
            <li key={e.id} className={li}><div className="font-semibold">{AUDIT[e.action] || e.action}</div>
              <div className="text-stone-500">{fmtDT(e.at)} · by {e.actor_email}</div>
              {(e.before || e.after) && <div className="mt-0.5">From: {fmtVal(e.before)}<br />To: {fmtVal(e.after)}</div>}
              {e.reason && <div className="italic text-stone-500 mt-0.5">“{e.reason}”</div>}</li>
          ))}</ul> : <Empty t="No account changes recorded yet." />}
        </Section>
      )}
    </div>
  );
}
